import { useTranslation } from "@vixi/localization";
import { MarketingKitItem, marketingKitItemTitle, ShallowCourse } from "@vixi/types";
import { ComponentProps, forwardRef, useEffect, useMemo, useState } from "react";
import { buttonVariants, Divider, HtmlButtonLink, Img, LoadingSpinner, Skeleton } from "@vixi/ui";
import { VariantProps } from "class-variance-authority";

// Assets
import DownloadIcon from "@assets/icons/download.svg?react";
import { assetUrl, toObjectUrl } from "@vixi/api";

interface MarketingKitItemProps extends ComponentProps<"div"> {
  course?: ShallowCourse;
  item: MarketingKitItem;
}

// Styling
const MarketingKitItemRoot = forwardRef<HTMLDivElement, ComponentProps<"div">>((props, ref) => (
  <div
    className="bg-paper flex h-75 w-max min-w-58.25 flex-0 flex-col rounded"
    ref={ref}
    {...props}
  />
));

type DownloadButtonProps = Omit<ComponentProps<"a">, "color"> &
  Partial<Pick<VariantProps<typeof buttonVariants>, "disabled">>;

const DownloadButton = forwardRef<HTMLAnchorElement, DownloadButtonProps>(
  ({ disabled, ...props }, ref) => (
    <HtmlButtonLink
      className="mt-0.5 px-2 py-0.5"
      size="content"
      variant="text"
      color="foreground"
      endIcon={<DownloadIcon />}
      ref={ref}
      {...props}
    >
      {props.children}
    </HtmlButtonLink>
  ),
);

const MarketingKitItemPreview = forwardRef<HTMLDivElement, MarketingKitItemProps>(
  ({ item, ...props }, ref) => {
    const { t } = useTranslation();

    const [downloadFileUrl, setDownloadFileUrl] = useState<string | undefined>();

    const clearDownloadFileUrl = () => {
      setDownloadFileUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return undefined;
      });
    };

    // Generate blob and create object URL for the download, clear it on unmount
    useEffect(() => {
      if (!item || !item.url) {
        setDownloadFileUrl(undefined);
        return;
      }

      (async () => {
        const url = await toObjectUrl(assetUrl(item.url));
        setDownloadFileUrl(url);
      })();

      return clearDownloadFileUrl;
    }, [item?.url]);

    const downloadFileName = useMemo(
      () =>
        `${props.course?.title}_${t(marketingKitItemTitle(item))}.${item.url
          .split(".")
          .pop()}`.replace(/\s/g, "-"),
      [props.course?.title, item.url, t],
    );

    return (
      <MarketingKitItemRoot {...props} ref={ref}>
        <div className="mb-5 flex min-h-0 flex-1 flex-col px-5 pt-3.5">
          <h4 className="w-max px-5 font-semibold">{t(marketingKitItemTitle(item))}</h4>
          <Img contain src={assetUrl(item.url)} className="mt-4 flex min-h-0 w-auto" />
        </div>

        <Divider />

        <div className="px-3 pt-1 pb-2">
          {downloadFileUrl ? (
            <DownloadButton href={downloadFileUrl} download={downloadFileName}>
              {t("Action.Download")}
            </DownloadButton>
          ) : (
            <DownloadButton disabled={true}>{t("Action.Download")}</DownloadButton>
          )}
        </div>
      </MarketingKitItemRoot>
    );
  },
);

const MarketingKitGridItem = ({ item, ...props }: Partial<MarketingKitItemProps>) => {
  if (!item)
    return (
      <Skeleton className="h-75 w-57.5 rounded">
        <div className="flex h-full w-full">
          <div className="m-auto h-fit w-fit">
            <LoadingSpinner />
          </div>
        </div>
      </Skeleton>
    );

  return <MarketingKitItemPreview item={item} {...props} />;
};

export { MarketingKitGridItem };
